import React, { useMemo } from 'react';
import {
  getFieldComponent,
  isDisplayOnlyField,
  FIELD_CATEGORIES,
} from './fieldRegistry';
import useFieldHandlers from '../../../../hooks/ui/formFields/useFieldHandlers';
import { getColumnStyle } from '../../../../utils/formCrafter/columnWidthUtils';
import { getValidatedFileConfig } from '../../../../utils/formCrafter/fileTypeUtils';
import { DEFAULT_FILE_CONFIG } from '../../../../constants/formCrafter.constant';

/**
 * IDSField - Orchestrator component that renders IDS (apollo-react) form fields
 * Follows atomic design principles by delegating to specific field atom components
 *
 * @param {Object} field - FormCrafter field schema
 * @param {any} value - Current field value
 * @param {Function} onChange - Callback when value changes
 * @param {Function} onBlur - Callback when field loses focus
 * @param {string} error - Error message to display
 * @param {boolean} disabled - Whether field is disabled
 * @param {boolean} touched - Whether field has been touched (for showing errors)
 */
const IDSField = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  touched = false,
  existingFiles = [],
  onServerFileDelete,
  // Props for eager file upload
  formId,
  subformId,
  onStagedFileUpload,
  onStagedFileDelete,
  eagerUploadEnabled = false,
  // Initial staged files from draft
  initialStagedFiles = [],
  // Enable pending delete flow (confirm/cancel)
  enablePendingDelete = false,
  // Ref forwarded to FileFieldInput (for imperative confirmPendingDeletions)
  fileRef,
  // Repeater child validation: full error/touched maps for distributing to children
  allErrors = {},
  allTouched = {},
  // Path prefix for nested repeater error key lookups
  errorPathPrefix,
}) => {
  const {
    id,
    type,
    label,
    placeholder,
    helpText,
    required,
    options = [],
    min,
    max,
    step,
    minRows,
    accept: customAccept,
    maxItems: customMaxItems,
    maxSize: customMaxSize,
    columnWidth,
    countryCode,
    defaultCurrency,
    conditions,
  } = field;

  const fileConfig = useMemo(() => {
    const defaults = DEFAULT_FILE_CONFIG[type] || DEFAULT_FILE_CONFIG.file;
    return getValidatedFileConfig(
      {
        type,
        accept: customAccept,
        maxItems: customMaxItems,
        maxSize: customMaxSize,
      },
      defaults
    );
  }, [type, customAccept, customMaxItems, customMaxSize]);

  const columnStyle = useMemo(() => getColumnStyle(columnWidth), [columnWidth]);

  const showError = touched && !!error;
  const helperText = showError ? error : helpText || '';

  // Use the field's configured maxItems, or fall back to fileConfig defaults
  const effectiveFileConfig = useMemo(() => fileConfig, [fileConfig]);

  const { handlers, normalizedValues } = useFieldHandlers(
    onChange,
    value,
    effectiveFileConfig
  );

  const normalizedOptions = useMemo(() => {
    if (!options || options.length === 0) return [];
    return options.map((opt) => ({
      label: opt.label || opt.value || String(opt),
      value: opt.value ?? opt.key ?? opt.label ?? String(opt),
    }));
  }, [options]);

  if (isDisplayOnlyField(type)) {
    return null;
  }

  const FieldComponent = getFieldComponent(type);

  const baseProps = {
    id,
    label,
    placeholder,
    helperText,
    showError,
    disabled,
    required,
    columnStyle,
  };

  if (FIELD_CATEGORIES.TEXT.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        type={type}
        value={value}
        onChange={handlers.handleTextChange}
        onBlur={onBlur}
        min={min}
        max={max}
        step={step}
        minRows={minRows}
      />
    );
  }

  if (FIELD_CATEGORIES.PHONE.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={handlers.handlePhoneChange}
        onBlur={onBlur}
        countryCode={countryCode}
      />
    );
  }

  if (FIELD_CATEGORIES.SELECT.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={handlers.handleSelectChange}
        onBlur={onBlur}
        options={normalizedOptions}
      />
    );
  }

  if (FIELD_CATEGORIES.CURRENCY.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={handlers.handleSelectChange}
        onBlur={onBlur}
        options={normalizedOptions}
      />
    );
  }

  if (FIELD_CATEGORIES.PRICE.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        options={normalizedOptions}
        defaultCurrency={defaultCurrency}
      />
    );
  }

  if (FIELD_CATEGORIES.DATE.includes(type)) {
    const isDateOnly = type === 'date';

    // IDS `DatePicker` emits a moment value (not an event), while IDS `TimePicker`
    // emits a string value. Route handlers accordingly to avoid `e.target.value`
    // crashes.
    if (isDateOnly) {
      return (
        <FieldComponent
          {...baseProps}
          type={type}
          value={value}
          onChange={handlers.handleDateChange}
          onBlur={onBlur}
        />
      );
    }

    // time / datetime / datetime-local are handled inside `DateFieldInput` and
    // will call `onChange` with a string payload.
    return (
      <FieldComponent
        {...baseProps}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }

  if (FIELD_CATEGORIES.RADIO.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={handlers.handleRadioChange}
        options={normalizedOptions}
      />
    );
  }

  if (FIELD_CATEGORIES.CHECKBOX.includes(type)) {
    const hasOptions = normalizedOptions.length > 0;
    return (
      <FieldComponent
        {...baseProps}
        value={
          hasOptions
            ? normalizedValues.checkboxValue
            : normalizedValues.booleanValue
        }
        onGroupChange={handlers.handleCheckboxGroupChange}
        onSingleChange={handlers.handleSingleCheckboxChange}
        options={normalizedOptions}
      />
    );
  }

  if (FIELD_CATEGORIES.SWITCH.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={normalizedValues.booleanValue}
        onChange={handlers.handleSwitchChange}
      />
    );
  }

  if (FIELD_CATEGORIES.FILE.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        ref={fileRef}
        value={normalizedValues.fileValue}
        onUpload={handlers.handleFileUpload}
        onDelete={handlers.handleFileDelete}
        onServerFileDelete={onServerFileDelete}
        fileConfig={effectiveFileConfig}
        existingFiles={existingFiles}
        // Eager upload props
        formId={formId}
        subformId={subformId}
        onStagedFileUpload={onStagedFileUpload}
        onStagedFileDelete={onStagedFileDelete}
        eagerUploadEnabled={eagerUploadEnabled}
        initialStagedFiles={initialStagedFiles}
        enablePendingDelete={enablePendingDelete}
      />
    );
  }

  if (FIELD_CATEGORIES.SIGNATURE.includes(type)) {
    return <FieldComponent {...baseProps} value={value} onChange={onChange} />;
  }

  if (FIELD_CATEGORIES.SLIDER.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
      />
    );
  }

  if (FIELD_CATEGORIES.RATING.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={onChange}
        max={max}
      />
    );
  }

  if (FIELD_CATEGORIES.TAGS.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={onChange}
        options={normalizedOptions}
      />
    );
  }

  if (FIELD_CATEGORIES.REPEATER.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        fields={field.fields}
        fieldId={id}
        allErrors={allErrors}
        allTouched={allTouched}
        errorPathPrefix={errorPathPrefix}
        existingFiles={existingFiles}
        onServerFileDelete={onServerFileDelete}
        formId={formId}
        subformId={subformId}
        onStagedFileUpload={onStagedFileUpload}
        onStagedFileDelete={onStagedFileDelete}
        eagerUploadEnabled={eagerUploadEnabled}
        initialStagedFiles={initialStagedFiles}
      />
    );
  }

  if (FIELD_CATEGORIES.HIDDEN.includes(type)) {
    return (
      <FieldComponent
        {...baseProps}
        value={value}
        onChange={handlers.handleTextChange}
        onBlur={onBlur}
        hasConditions={conditions && conditions.length > 0}
      />
    );
  }

  return (
    <FieldComponent
      {...baseProps}
      type="text"
      value={value}
      onChange={handlers.handleTextChange}
      onBlur={onBlur}
    />
  );
};

export default IDSField;
